// ============================================================================
// CHANGO V5.0 — Product autofill helper (Task #66 · D3 × D5 bridge)
// ============================================================================
// Pure logic for computing the diff-patch that QuoteWizard applies to a line
// item when the user picks a product from the catalog.
//
// Policy (UEDA, 2026-04-25):
//   1. SKU is ALWAYS overwritten — picking a product IS signal to use it.
//   2. description is only overwritten if the current description is blank.
//   3. unit_sell_rmb is only overwritten if the current value is 0 AND the
//      product has a positive default_price_rmb. Never clobber a non-zero
//      manual price — the user may have negotiated a custom rate.
//   4. unit_cost_rmb is only overwritten if the current value is 0 AND the
//      product exposes default_cost_rmb (boss/finance projection — sales
//      never sees it and the wizard is gated off sales). If the product
//      lacks cost, we leave unit_cost_rmb alone; the wizard validates it
//      at a later step.
//   5. HS code / rebate metadata may be prefilled from the selected product
//      when present, but it is not locked to SKU. Users can independently
//      search by commodity category / goods name / HS code and override it.
//
// Extracted from QuoteWizard.jsx so it can be unit-tested without JSDOM.
// ============================================================================

export function toNumber(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Compute the shallow patch to merge into a line item when a product is
 * selected.
 *
 * @param {object} row      Current line_items row (may be empty / new)
 * @param {object} product  Product row from /api/products
 * @returns {object}        Patch to spread into the row. Always contains at
 *                          least `sku`. May contain description / prices
 *                          depending on the row's current state.
 */
export function computeAutofillPatch(row, product) {
  if (!product || typeof product !== 'object') return {};
  const base = row && typeof row === 'object' ? row : {};

  const patch = { sku: product.sku_code ?? '' };

  // Description — only when empty.
  const curDesc = base.description;
  if (!curDesc || String(curDesc).length === 0) {
    patch.description = product.name || '';
  }

  // Sell price — only when current is 0 and product price is > 0.
  const curSell = toNumber(base.unit_sell_rmb);
  const prodSell = toNumber(product.default_price_rmb);
  if (curSell === 0 && prodSell > 0) {
    patch.unit_sell_rmb = prodSell;
  }

  // Cost price — only when present on the product AND current is 0.
  if (product.default_cost_rmb != null) {
    const curCost = toNumber(base.unit_cost_rmb);
    const prodCost = toNumber(product.default_cost_rmb);
    if (curCost === 0 && prodCost >= 0) {
      patch.unit_cost_rmb = prodCost;
    }
  }

  if (product.hs_code != null) {
    patch.hs_code = product.hs_code || '';
  }
  if (product.tax_rebate_rate != null || product.rebate_rate != null) {
    patch.tax_rebate_rate = product.tax_rebate_rate ?? product.rebate_rate;
  }
  if (product.tax_rebate_source != null || product.rebate_source != null) {
    patch.tax_rebate_source = product.tax_rebate_source ?? product.rebate_source;
  }

  return patch;
}
